import type { LocalizedString } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { AlertMessage } from "@/components/ui/alert-message";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { LocalizedInput } from "@/components/ui/localized-input";
import { ApiError } from "@/lib/api/_client";
import {
  useCreateRiskModel,
  useCreateRiskModelFromTemplate,
  useUpdateRiskModel,
} from "@/lib/hooks/use-risk-models";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./risk-model-dialog.i18n";

type TemplateKey = "simple" | "standard" | "detailed" | "blank";

type RiskModelDialogProps = {
  open: boolean;
  model?: any | null;
  onClose: () => void;
};

export function RiskModelDialog({ open, model, onClose }: RiskModelDialogProps) {
  const navigate = useNavigate();
  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);

  const editingModelName = model ? localize(model.nameI18n) : "";

  const createMutation = useCreateRiskModel();
  const templateMutation = useCreateRiskModelFromTemplate();
  const updateMutation = useUpdateRiskModel();

  // Template definitions for UI
  const TEMPLATE_OPTIONS = [
    {
      key: "simple" as const,
      label: t.templateSimple,
      subtitle: t.templateSimpleSubtitle,
      description: t.templateSimpleDescription,
      defaultName: t.templateSimpleDefaultName,
    },
    {
      key: "standard" as const,
      label: t.templateStandard,
      subtitle: t.templateStandardSubtitle,
      description: t.templateStandardDescription,
      defaultName: t.templateStandardDefaultName,
    },
    {
      key: "detailed" as const,
      label: t.templateDetailed,
      subtitle: t.templateDetailedSubtitle,
      description: t.templateDetailedDescription,
      defaultName: t.templateDetailedDefaultName,
    },
    {
      key: "blank" as const,
      label: t.templateBlank,
      subtitle: t.templateBlankSubtitle,
      description: t.templateBlankDescription,
      defaultName: "",
    },
  ];

  const [selectedTemplate, setSelectedTemplate] = useState<TemplateKey>("standard");
  const [name, setName] = useState("");
  const [nameI18n, setNameI18n] = useState<LocalizedString>({});
  const [description, setDescription] = useState("");
  const [descriptionI18n, setDescriptionI18n] = useState<LocalizedString>({});
  const isPending =
    createMutation.isPending || templateMutation.isPending || updateMutation.isPending;

  // Alert state
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  useEffect(() => {
    if (model) {
      setName(localize(model.nameI18n));
      setNameI18n(model.nameI18n);
      setDescription(model.descriptionI18n ? localize(model.descriptionI18n) : "");
      setDescriptionI18n(model.descriptionI18n || {});
      setSelectedTemplate("blank"); // Editing doesn't use templates
    } else {
      setSelectedTemplate("standard");
      const defaultTemplate = TEMPLATE_OPTIONS.find((tmpl) => tmpl.key === "standard");
      setName(defaultTemplate?.defaultName || "");
      setNameI18n({});
      setDescription(defaultTemplate?.description || "");
      setDescriptionI18n({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, open]);

  // Update name and description when template changes (only for new models)
  const handleTemplateChange = (key: TemplateKey) => {
    setSelectedTemplate(key);
    if (!model) {
      const template = TEMPLATE_OPTIONS.find((tmpl) => tmpl.key === key);
      setName(template?.defaultName || "");
      setDescription(template?.description || "");
    }
  };

  const showAlert = (
    title: string,
    alertDescription: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description: alertDescription, variant });
    setAlertOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    // Build the full i18n objects with the primary language value
    const fullNameI18n: LocalizedString = {
      ...nameI18n,
      [userLanguage]: name,
    };

    const fullDescriptionI18n: LocalizedString | undefined = description.trim()
      ? {
          ...descriptionI18n,
          [userLanguage]: description,
        }
      : undefined;

    try {
      if (model) {
        await updateMutation.mutateAsync({
          id: model.id,
          data: { nameI18n: fullNameI18n, descriptionI18n: fullDescriptionI18n },
        });
        onClose();
      } else if (selectedTemplate === "blank") {
        const created = await createMutation.mutateAsync({
          nameI18n: fullNameI18n,
          descriptionI18n: fullDescriptionI18n,
        });
        onClose();
        navigate(`/settings/risk-models/${created.id}`);
      } else {
        const created = await templateMutation.mutateAsync({
          template: selectedTemplate,
          nameI18n: fullNameI18n,
          descriptionI18n: fullDescriptionI18n,
        });
        onClose();
        navigate(`/settings/risk-models/${created.id}`);
      }
    } catch (err) {
      showAlert(t.error, err instanceof ApiError ? err.message : t.failedToSave, "destructive");
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {model
                ? editingModelName
                  ? `${t.editTitle} (${editingModelName})`
                  : t.editTitle
                : t.createTitle}
            </DialogTitle>
            <DialogDescription>{model ? t.editDescription : t.createDescription}</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Template selector - only show when creating new */}
            {!model && (
              <div className="space-y-3">
                <Label>{t.chooseTemplate}</Label>
                <div className="space-y-2">
                  {TEMPLATE_OPTIONS.map((template) => (
                    <button
                      key={template.key}
                      type="button"
                      onClick={() => handleTemplateChange(template.key)}
                      className={cn(
                        "w-full text-left p-3 rounded-lg border transition-colors",
                        selectedTemplate === template.key
                          ? "border-primary bg-primary/5 ring-1 ring-primary"
                          : "border-border hover:border-muted-foreground/50 hover:bg-muted/50",
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <div
                            className={cn(
                              "w-4 h-4 rounded-full border-2 flex items-center justify-center",
                              selectedTemplate === template.key
                                ? "border-primary"
                                : "border-muted-foreground/50",
                            )}
                          >
                            {selectedTemplate === template.key && (
                              <div className="w-2 h-2 rounded-full bg-primary" />
                            )}
                          </div>
                          <span className="font-medium">{template.label}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">{template.subtitle}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 ml-6">
                        {template.description}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <LocalizedInput
              id="name"
              label={t.name}
              value={name}
              i18nValue={nameI18n}
              onChange={setName}
              onI18nChange={setNameI18n}
              supportedLanguages={tenantSupportedLanguages}
              placeholder={t.namePlaceholder}
              required
            />

            <LocalizedInput
              id="description"
              label={t.description}
              value={description}
              i18nValue={descriptionI18n}
              onChange={setDescription}
              onI18nChange={setDescriptionI18n}
              supportedLanguages={tenantSupportedLanguages}
              placeholder={t.descriptionPlaceholder}
              multiline
              rows={2}
            />

            <DialogFooter>
              <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
              <Button type="submit" loading={isPending}>
                {model ? t.save : t.create}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
